import React from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { OneWayFeeActionTypes } from '../../types/onewayfees.types';
import Select from '../../../../components/forms/Select';
import { AppState } from '../../../../store/store.types';
import {
  oneWayFeeEditRoles,
  OneWayFeeItemPriceModel,
  OneWayFeeItemPriceRequest,
  OneWayFeeModel,
  OneWayFeePriceItem,
  OneWayFeeRequest
} from '../../../../api/pricing/pricing.types';
import { DeskModel } from '../../../../api/core/core.types';
import { getDeskList } from '../../../setup/actions/desk.actions';
import { addOneWayFees, getOneWayFeeList } from '../../actions/onewayfee.actions';
import { WithTranslation, withTranslation } from 'react-i18next';
import { FieldControl, FieldGroup, FormBuilder } from 'react-reactive-form';
import { editOneWayFeesItemPrice, getOneWayFeeItemPrice } from '../../actions/onewayfeeitemprice.actions';
import OneWayFeeTable from '../../../../components/tables/OneWayFeeTable';
import { LoaderBar } from '../../../../components/forms/LoaderBar';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import SectionTitle from '../../../../components/forms/SectionTitle';
import { SupplierModel } from '../../../../api/admin/admin.types';
import { formatDateTime } from '../../../../helpers/date.helper';
import { Link } from 'react-router-dom';
import Checkbox from '../../../../components/forms/Checkbox';

interface OneWayFeesProps extends RouteComponentProps, WithTranslation {
  oneWayFees: OneWayFeeModel[];
  desks: DeskModel[];
  getDesks: () => void;
  getOneWayFees: () => Promise<void>;
  getOneWayFeesItemPrice: () => void;
  itemPrice: OneWayFeeItemPriceModel | null;
  user: UserModel | null;
  supplier: SupplierModel | null;
  editOneWayFeesItemPrice: (oneWayFeesItemPriceRequest: OneWayFeeItemPriceRequest) => void;
}

interface OneWayFeesState {
  filtered: { value: any, id: any }[];
  columns: { label: string; width?: string; code?: string }[];
  isEdit: boolean;
  filter: {
    pickupLocationFilter: {
      value: any;
    };
    returnLocationFilter: {
      value: any;
    };
  };
  waitingForResponse: boolean;
  showTable: boolean;
  isTaxable: boolean;
  isTaxIncluded: boolean;
  isEditOneModalOpen: boolean;
  selectedOneWayFee?: OneWayFeeModel;
  changedPriceValue?: OneWayFeePriceItem;
  filteredPickupDesks: DeskModel[];
  filteredReturnDesks: DeskModel[];
}

class OneWayFees extends React.Component<OneWayFeesProps, OneWayFeesState> {

  constructor(props: OneWayFeesProps) {
    super(props);
    this.state = {
      filtered: [],
      columns: [],
      isEdit: false,
      filter: {
        pickupLocationFilter: {
          value: undefined
        },
        returnLocationFilter: {
          value: undefined
        }
      },
      waitingForResponse: true,
      showTable: false,
      isTaxable: false,
      isTaxIncluded: false,
      isEditOneModalOpen: false,
      selectedOneWayFee: undefined,
      filteredPickupDesks: [],
      filteredReturnDesks: []
    };

  }

  form = FormBuilder.group({
    isTaxable: [ null ],
    isTaxIncluded: [ null ]
  });

  async componentDidMount() {
    await this.props.getDesks();
    this.props.getOneWayFeesItemPrice();
    await this.getOneWayFees();
    this.props.getOneWayFeesItemPrice();
    this.setState({ waitingForResponse: false });

  }

  async componentDidUpdate(prevProps: OneWayFeesProps) {
    if (this.props.itemPrice && this.props.itemPrice !== prevProps.itemPrice) {
      this.setState({
        isTaxable: this.props.itemPrice.isTaxable,
        isTaxIncluded: this.props.itemPrice.isTaxIncluded
      });
      this.updateFormValue();
    }

  }

  getOneWayFees = async () => {
    await this.props.getOneWayFees();
    this.setState({ waitingForResponse: false });
  };

  updateFormValue = () => {
    if (!this.props.itemPrice) {
      return;
    }
    this.form.patchValue({
      isTaxable: this.props.itemPrice.isTaxable,
      isTaxIncluded: this.props.itemPrice.isTaxIncluded
    });
  };

  handleFilterValChange = () => {
    const filter = this.state.filter;
    this.setState({
      filtered: [
        { value: filter.pickupLocationFilter.value, id: 'pickupDeskCode' },
        { value: filter.returnLocationFilter.value, id: 'returnDeskCode' }
      ]
    });
  };

  dropdownChange = (newValue: any, field: string) => {
    this.setState({
      filter: {
        ...this.state.filter,
        [ field ]: {
          value: newValue
        }
      }
    }, () => {
      this.handleFilterValChange();
    });
  };

  sendItemPrice() {
    const owfItemPriceRequest: OneWayFeeItemPriceRequest = {
      isTaxable: this.form.controls.isTaxable === undefined ? false : this.form.controls.isTaxable.value,
      isTaxIncluded: this.form.controls.isTaxIncluded === undefined ? false : this.form.controls.isTaxIncluded.value
    };

    if (this.props.itemPrice) {
      this.props.editOneWayFeesItemPrice(owfItemPriceRequest);
    }
  }

  getTaxesCheckBox() {
    const { t } = this.props;

    return (
      <FieldGroup
        strict={false}
        control={this.form}
        render={() => (
          <>
            <div className="form-group row">
              <div className="col-4 d-flex align-items-center justify-content-end">
                {this.state.isEdit && (
                  <label className="mandatory-field">* </label>
                )}
                <label>{t('l.item_price.taxable')}:</label>
              </div>
              <div className="col-6 d-flex align-items-center justify-content-start">
                <FieldControl
                  strict={false}
                  name="isTaxable"
                  render={(props) => <Checkbox {...props} id="isTaxable" className="mt-1"
                                               disabled={!this.state.isEdit}></Checkbox>}
                />
              </div>
            </div>
            <div className="form-group row">
              <div className="col-4 d-flex align-items-center justify-content-end">
                {this.state.isEdit && (
                  <label className="mandatory-field">* </label>
                )}
                <label>{t('l.item_price.tax_included')}:</label>
              </div>
              <div className="col-6 d-flex align-items-center justify-content-start">
                <FieldControl
                  strict={false}
                  name="isTaxIncluded"
                  render={(props) =>
                    <Checkbox {...props} id="isTaxIncluded" className="mt-1" disabled={!this.state.isEdit}></Checkbox>}
                />
              </div>
            </div>
          </>
        )}/>
    );
  }

  render() {
    const { t } = this.props;
    const pickupDesks = this.state.filter.pickupLocationFilter.value?.value ? this.props.desks.filter((desk: DeskModel) => desk.code === this.state.filter.pickupLocationFilter.value?.value) : this.props.desks;
    const returnDesks = this.state.filter.returnLocationFilter.value?.value ? this.props.desks.filter((desk: DeskModel) => desk.code === this.state.filter.returnLocationFilter.value?.value) : this.props.desks;
    return (
      <main>
        <div className="page-content">
          <div className="d-flex justify-content-start align-items-center">
            <h1>{t('page_title.one_way_fees')}</h1>
          </div>
          <div className="mt-2">
            <SectionTitle
              title={t('section_title.owf.general_settings').toUpperCase()}
              showButton={!this.state.isEdit && this.props.user !== null && userCanAccessComponent(this.props.user, oneWayFeeEditRoles)}
              onEditClick={() => this.setState({ isEdit: true })}
              buttonName={t('b.edit_Generic edit button').toUpperCase()}
              showSaveButton={this.state.isEdit}
              showCancelButton={this.state.isEdit}
              onSaveClick={() => {
                this.sendItemPrice();
                this.setState({ isEdit: false });
              }}
              saveButtonName={t('b.save_Generic save button translation').toUpperCase()}
              onCancelClick={() => {
                this.setState({ isEdit: false });
                this.updateFormValue();
              }}
              cancelButtonName={t('b.cancel_Generic cancel button translation').toUpperCase()}
            />
            <div className="col-12 mt-1">
              <div className="pl-1">
                <form className="form-horizontal">
                  {this.getTaxesCheckBox()}
                </form>
              </div>
            </div>
            {this.props.itemPrice && (
              <>
                <SectionTitle title={t('section_title.audit').toUpperCase()}/>
                <div className="d-flex">
                  <div className="col-8">
                    <div className="d-flex row">
                      <label
                        className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.created_by')}: </label>
                      <div className="col-4 d-flex align-items-center justify-content-start">
                        {this.props.itemPrice.createdBy ?? 'System'}{this.props.itemPrice.createdAt ? ` @ ${formatDateTime(this.props.itemPrice.createdAt)}` : ''}
                      </div>
                      <label
                        className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.modified_by')}: </label>
                      <div className="col-4 d-flex align-items-center justify-content-start">
                        {this.props.itemPrice.modifiedBy ?? 'System'}{this.props.itemPrice.modifiedAt ? ` @ ${formatDateTime(this.props.itemPrice.modifiedAt)}` : ''}
                      </div>
                      <label
                        className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.log')}: </label>
                      <div className="col-4 d-flex align-items-center justify-content-start">
                        <Link to={`/rates/itemprices/${this.props.itemPrice.id}/audit`}
                              className="btn btn-text-only"
                              target="_blank"
                              rel="noopener noreferrer">
                          {t('b.car_model.view_audit_log')}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            <SectionTitle
              title={t('section_title.owf.prices').toUpperCase()}
            />
            <div className="col-12 mt-1">
              <div className="fees-container p-1">
                <form
                  className="container-fluid filter-container form-group form-inline d-flex justify-content-between">
                  <div className="d-flex row ml-1 filters-container-margin-top-btm">
                    <Select
                      value={this.state.filter.pickupLocationFilter.value}
                      className="mr-1"
                      equalWidthElement
                      elWidth={240}
                      placeholder={t('ph.filter.one_way_fees.by_pickup_desk')}
                      options={this.props.desks.map((d) => ({ label: `${d.name} (${d.code})`, value: d.code }))}
                      onChange={(val) => this.dropdownChange(val, 'pickupLocationFilter')}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      label="pickupLocation"
                    />
                    <Select
                      value={this.state.filter.returnLocationFilter.value}
                      equalWidthElement
                      className="mr-1"
                      elWidth={240}
                      placeholder={t('ph.filter.one_way_fees.by_return_desk')}
                      options={this.props.desks.map((d) => ({ label: `${d.name} (${d.code})`, value: d.code }))}
                      onChange={(val) => this.dropdownChange(val, 'returnLocationFilter')}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      label="returnLocation"
                    />
                  </div>
                </form>
                <div className="fees-container-body p-1 m-2">
                  {this.state.waitingForResponse ? <LoaderBar/> : ''}
                  {!!this.props.desks.length && (
                    <OneWayFeeTable
                      waitingForResponse={(status: boolean) => this.setState({ waitingForResponse: status })}
                      oneWayFees={this.props.oneWayFees}
                      getOneWayFees={() => this.getOneWayFees()}
                      pickupDesks={pickupDesks}
                      returnDesks={returnDesks}
                      supplierInfo={this.props.supplier}
                      canEdit={this.props.user !== null && userCanAccessComponent(this.props.user, oneWayFeeEditRoles) && !this.state.waitingForResponse}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  desks: state.setup.desks.desks,
  oneWayFees: state.rates.oneWayFees.oneWayFees,
  itemPrice: state.rates.oneWayFeeItemPrice.itemPrice,
  user: state.authentication.user,
  supplier: state.authentication.activeSupplier
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, OneWayFeeActionTypes>) => ({
  getDesks: () => dispatch(getDeskList()),
  getOneWayFees: () => dispatch(getOneWayFeeList()),
  addOneWayFees: (oneWayFees: OneWayFeeRequest[]) => dispatch(addOneWayFees(oneWayFees)),
  editOneWayFeesItemPrice: (oneWayFeesItemPrice: OneWayFeeItemPriceRequest) => dispatch(editOneWayFeesItemPrice(oneWayFeesItemPrice)),
  getOneWayFeesItemPrice: () => dispatch(getOneWayFeeItemPrice())
});

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(OneWayFees));
